/**
 * Conversions between the UTC `push_send_at` value stored on a notification and the wall-clock
 * time a `datetime-local` input shows.
 *
 * Office runs in whatever timezone the user's browser is in, but marketing schedules a send for a
 * time in the target country. Converting through `Intl` keeps this dependency-free.
 */

export const COUNTRY_TIMEZONES = {
    NL: 'Europe/Amsterdam',
    BE: 'Europe/Brussels',
    DE: 'Europe/Berlin',
    ES: 'Europe/Madrid',
} as const

export type NotificationCountryCode = keyof typeof COUNTRY_TIMEZONES

export const timeZoneForCountry = (countryCode?: string): string =>
    COUNTRY_TIMEZONES[(countryCode as NotificationCountryCode) ?? 'NL'] ?? COUNTRY_TIMEZONES.NL

export const timeZoneLabelForCountry = (countryCode?: string): string => {
    const timeZone = timeZoneForCountry(countryCode)
    const abbreviation = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        timeZoneName: 'short',
    })
        .formatToParts(new Date())
        .find((part) => part.type === 'timeZoneName')?.value

    return abbreviation ? `${timeZone} (${abbreviation})` : timeZone
}

interface ZonedParts {
    year: string
    month: string
    day: string
    hour: string
    minute: string
    second: string
}

const partsInZone = (date: Date, timeZone: string): ZonedParts => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(date)

    const find = (type: Intl.DateTimeFormatPartTypes) =>
        parts.find((part) => part.type === type)?.value ?? '00'

    return {
        year: find('year'),
        month: find('month'),
        day: find('day'),
        // Intl renders midnight as "24" in some environments.
        hour: find('hour') === '24' ? '00' : find('hour'),
        minute: find('minute'),
        second: find('second'),
    }
}

/** How far ahead of UTC the zone is at the given instant, in milliseconds. */
const zoneOffsetMs = (date: Date, timeZone: string): number => {
    const { year, month, day, hour, minute, second } = partsInZone(date, timeZone)
    const asIfUtc = Date.parse(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`)

    return asIfUtc - date.getTime()
}

const parseUtc = (value: string): Date | null => {
    // MySQL hands back 'YYYY-MM-DD HH:mm:ss', the API serializer an ISO string with an offset.
    const normalised = /[Zz]|[+-]\d{2}:?\d{2}$/.test(value)
        ? value.replace(' ', 'T')
        : `${value.replace(' ', 'T')}Z`
    const date = new Date(normalised)

    return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Turns a stored UTC timestamp into the `YYYY-MM-DDTHH:mm` value a datetime-local input needs,
 * expressed in the country's timezone.
 */
export const utcToCountryInputValue = (utcValue?: string | null, countryCode?: string): string => {
    if (!utcValue) return ''

    const date = parseUtc(utcValue)
    if (date === null) return ''

    const { year, month, day, hour, minute } = partsInZone(date, timeZoneForCountry(countryCode))

    return `${year}-${month}-${day}T${hour}:${minute}`
}

/**
 * Turns the wall-clock value from a datetime-local input, read as a time in the country's
 * timezone, into the `YYYY-MM-DD HH:mm:ss` UTC string the API stores.
 */
export const countryInputValueToUtc = (inputValue: string, countryCode?: string): string | null => {
    if (!inputValue) return null

    const asIfUtc = Date.parse(
        `${inputValue.length === 16 ? inputValue : inputValue.slice(0, 16)}:00Z`,
    )
    if (Number.isNaN(asIfUtc)) return null

    const timeZone = timeZoneForCountry(countryCode)
    // Two passes: the first offset is read at the wrong instant, which only matters within an hour
    // of a DST transition, and the second pass settles it.
    let timestamp = asIfUtc - zoneOffsetMs(new Date(asIfUtc), timeZone)
    timestamp = asIfUtc - zoneOffsetMs(new Date(timestamp), timeZone)

    return new Date(timestamp).toISOString().replace('T', ' ').slice(0, 19)
}

/** Human-readable rendering of a stored UTC timestamp in the country's timezone. */
export const formatSendAtForCountry = (utcValue?: string | null, countryCode?: string): string => {
    if (!utcValue) return ''

    const date = parseUtc(utcValue)
    if (date === null) return ''

    const { year, month, day, hour, minute } = partsInZone(date, timeZoneForCountry(countryCode))

    return `${day}-${month}-${year} ${hour}:${minute}`
}
